import { RefObject } from 'react';

import { GridApiPro, GridRowModelUpdate, GridValidRowModel } from '@mui/x-data-grid-pro';
import * as dateFns from 'date-fns';
import pLimit from 'p-limit';

import { Apuracao, DreListProps } from '../types';
import { findVlItem, getDreId } from '../utils';
import { useDreFilterLojas } from './useDreFilterLojas';

import { useApiContext } from 'context/ApiContext';

export function useDreListCalculate({ grid }: { grid: RefObject<GridApiPro | null> }) {
    const filterLojas = useDreFilterLojas();
    const isMultipleLojas = filterLojas?.value.length > 1;
    const { api } = useApiContext();
    const limit = pLimit(6); // Limita para 6 solicitações concorrentes

    /**
     * Faz o calculo da DRE informada, limitando 6 requests por vez.
     */
    const getDreCalculo = (dreProps: GridValidRowModel): Promise<DreListProps> => {
        const dtInicio = dateFns.parseISO(dreProps?.dtInicio);
        const idAno = dtInicio.getUTCFullYear();
        const idMes = dtInicio.getUTCMonth() + 1;
        const { idLoja } = dreProps.loja;

        return limit(() =>
            api.post(
                '/dre/dremercadologica/calcula',
                {},
                {
                    params: { idLoja, idAno, idMes },
                },
            ).then((res) => res.data.data),
        );
    };

    /**
     * Atualiza a row com os dados informados.
     */
    const updateRow = (row: GridRowModelUpdate) => {
        grid?.current?.updateRows([row]);
    };

    /**
     * Realiza o calculo mensal das DREs informadas.
     */
    const calculateRows = (rows: DreListProps[]) => {
        if (!isMultipleLojas) {
            rows.forEach((rowProps) => {
                updateRow({
                    id: getDreId(rowProps),
                    error: '',
                    isLoading: true,
                });

                getDreCalculo(rowProps)
                    .then((newDreProps) => {
                        updateRow({
                            id: getDreId(rowProps),
                            idStatus: newDreProps.dreStatus.idDreStatus,
                            idDreMercadologica: newDreProps.idDreMercadologica,
                            vlCmv: findVlItem(newDreProps, Apuracao.CMV),
                            vlGastos: findVlItem(newDreProps, Apuracao.GASTOS),
                            vlReceitaBruta: findVlItem(newDreProps, Apuracao.RECEITA_BRUTA),
                            vlLucroLiquido: findVlItem(newDreProps, Apuracao.LUCRO_LIQUIDO),
                            vlReceitaLiquida: findVlItem(newDreProps, Apuracao.RECEITA_LIQUIDA),
                            isLoading: false,
                        });
                    })
                    .catch((error) => {
                        updateRow({
                            id: getDreId(rowProps),
                            isLoading: false,
                            error: error?.data?.message || error?.data?.error || 'Falha ao calcular DRE',
                        });
                    });
            });
        }
    };

    return { calculateRows };
}
